import * as Actions from "./actions";
import * as States from "./states";
type Action<T> = Actions.Action<T>;
type State<T> = States.State<T>;

function reduceUnfocused<T>(
  state: States.Unfocused<T>,
  action: Action<T>
): State<T> {
  if (Actions.isGotFocus(action)) {
    const hasSelection = !!state.selection;
    const newState = hasSelection
      ? {
          ...state,
          tag: "Focused",
          loading: false,
          hasInputFocus: true,
        }
      : {
          ...state,
          tag: "SuggestionsLoading",
          loading: true,
          hasInputFocus: true,
        };

    return newState as State<T>;
  }

  if (Actions.isSelect(action)) {
    return {
      ...state,
      selection: action.selection,
      loading: false,
      errorMessage: "",
      text: state.format(action.selection),
    };
  }
  if (Actions.isClearSelection(action)) {
    return {
      ...state,
      selection: null,
      text: "",
    };
  }
  return state;
}

function reduceFocused<T>(
  state: States.Focused<T>,
  action: Action<T>
): State<T> {
  if (Actions.isLostFocus(action)) {
    return {
      ...state,
      tag: "Unfocused",
      hasInputFocus: false,
      hasMenuMouse: false,
      errorMessage: state.required && !state.selection ? "Required" : "",
    };
  }
  if (Actions.isTextChanged(action)) {
    return {
      ...state,
      text: action.text,
      selection: null,
      errorMessage: "",
    };
  }
  if (Actions.isLoadSuggestions(action)) {
    return {
      ...state,
      tag: "SuggestionsLoading",
      loading: true,
    };
  }
  if (Actions.isSelect(action)) {
    return {
      ...state,
      selection: action.selection,
      loading: false,
      errorMessage: "",
      text: state.format(action.selection),
    };
  }
  return state;
}

function reduceSuggestionsLoading<T>(
  state: States.SuggestionsLoading<T>,
  action: Action<T>
): State<T> {
  if (Actions.isSuggestionsLoaded(action)) {
    if ((!action.data || action.data.length === 0) && state.text) {
      return {
        ...state,
        tag: "Focused",
        errorMessage: state.emptyResultsMessage || "No results found",
        loading: false,
        hasMenuMouse: false,
      };
    }
    if ((!action.data || action.data.length === 0) && !state.text) {
      return {
        ...state,
        loading: false,
        tag: "Focused",
      };
    }
    return {
      ...state,
      tag: "SuggestionsVisible",
      suggestionIndex: 0,
      suggestions: action.data,
      loading: false,
    };
  }
  if (Actions.isTextChanged(action)) {
    return {
      ...state,
      loading: false,
      tag: "Focused",
      text: action.text,
      selection: null,
      errorMessage: "",
    };
  }
  if (Actions.isSelect(action)) {
    return {
      ...state,
      tag: "Focused",
      selection: action.selection,
      loading: false,
      errorMessage: "",
      text: state.format(action.selection),
    };
  }
  return state;
}

function reduceSuggestionsVisible<T>(
  state: States.SuggestionsVisible<T>,
  action: Action<T>
): State<T> {
  if (Actions.isLostFocus(action) && state.suggestions.length === 1) {
    return {
      ...state,
      tag: "Unfocused",
      selection: state.suggestions[0],
      text: state.format(state.suggestions[0]),
      errorMessage: "",
      hasInputFocus: false,
      hasMenuMouse: false,
    };
  }
  if (Actions.isLostFocus(action) && !state.hasMenuMouse) {
    return {
      ...state,
      tag: "Unfocused",
      hasInputFocus: false,
      hasMenuMouse: false,
      errorMessage: state.required && !state.selection ? "Required" : "",
    };
  }
  if (Actions.isTextChanged(action)) {
    return {
      ...state,
      tag: "Focused",
      text: action.text,
      hasInputFocus: true,
      errorMessage: "",
    };
  }
  if (Actions.isMoveHighlightUp(action) && state.suggestionIndex > 0) {
    return {
      ...state,
      suggestionIndex: state.suggestionIndex - 1,
    };
  }
  if (
    Actions.isMoveHighlightDown(action) &&
    state.suggestionIndex < state.suggestions.length - 1
  ) {
    return {
      ...state,
      suggestionIndex: state.suggestionIndex + 1,
    };
  }
  if (Actions.isGotMenuMouse(action)) {
    return { ...state, hasMenuMouse: true };
  }
  if (Actions.isLostMenuMouse(action)) {
    if (!state.hasInputFocus) {
      return {
        ...state,
        tag: "Unfocused",
        hasMenuMouse: false,
        hasInputFocus: false,
      };
    }
    return {
      ...state,
      hasMenuMouse: false,
    };
  }
  if (Actions.isSelect(action)) {
    return {
      ...state,
      tag: "Focused",
      selection: action.selection,
      errorMessage: "",
      text: state.format(action.selection),
    };
  }

  return state;
}

export function reducer<T>(state: State<T>, action: Action<T>): State<T> {
  if (States.isFocused(state)) {
    return reduceFocused(state, action);
  }
  if (States.isUnfocused(state)) {
    return reduceUnfocused(state, action);
  }
  if (States.isSuggestionsVisible(state)) {
    return reduceSuggestionsVisible(state, action);
  }
  if (States.isSuggestionsLoading(state)) {
    return reduceSuggestionsLoading(state, action);
  }
  return state;
}
